import React, { useState, useRef, useEffect, useCallback } from 'react';
import Webcam from 'react-webcam';
import './App.css';

// =============================================
// 🧠 CONSTANTES
// =============================================
const PIXEL_SIZE = 18;
const FRAME_READY_THRESHOLD = 15;
const CAPTURE_DELAY_MS = 100; // Reducimos el delay para mayor inmediatez

function App() {
    const [currentView, setCurrentView] = useState('landing');
    const [showPreview, setShowPreview] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const [cameraActive, setCameraActive] = useState(false);
    const [facingMode, setFacingMode] = useState('environment');
    const [activeEffects, setActiveEffects] = useState({
        streakBlur: true,
        pixelation: true,
        colorEffects: false,
        rgbSplit: true, // 👈 NUEVO: Activado por defecto
    });
    const [isCapturing, setIsCapturing] = useState(false);
    const [cameraReady, setCameraReady] = useState(false);

    const webcamRef = useRef(null);
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const framesProcessedRef = useRef(0);
    const isCameraReadyRef = useRef(false);
    const frameBufferRef = useRef([]);
    const BUFFER_SIZE = 5;

    const [textImage, setTextImage] = useState(null);
    const [logoImage, setLogoImage] = useState(null);

    useEffect(() => {
        const textImg = new Image();
        textImg.src = process.env.PUBLIC_URL + '/assets/img/watermark.png';
        textImg.onload = () => {
            setTextImage(textImg);
        };

        const logoImg = new Image();
        logoImg.src = process.env.PUBLIC_URL + '/LogoLynk&Co-02.png';
        logoImg.onload = () => {
            setLogoImage(logoImg);
        };
    }, []);

// ----------------------------------------------------
// FUNCIONES DE EFECTOS (CON EL NUEVO RGB SPLIT)
// ----------------------------------------------------

    // 🎭 EFECTO BARRIDO MEJORADO
    const applyStreakEffect = useCallback((ctx, width, height) => {
        if (frameBufferRef.current.length < 2) return;

        ctx.globalCompositeOperation = 'lighten';
        
        for (let i = 1; i < Math.min(frameBufferRef.current.length, 4); i++) {
            const frame = frameBufferRef.current[i];
            const opacity = 0.3 + (0.15 * i);
            const offset = i * 16; 
            
            ctx.globalAlpha = opacity;
            ctx.drawImage(frame, offset, 0, width, height);
        }

        ctx.globalAlpha = 1.0;
        ctx.globalCompositeOperation = 'source-over';
    }, []);

    // 🎨 PIXELADO
    const applyPixelation = useCallback((ctx, width, height) => {
        const tempCanvas = document.createElement('canvas');
        const pixelSize = PIXEL_SIZE;
        const smallWidth = Math.floor(width / pixelSize);
        const smallHeight = Math.floor(height / pixelSize);
        
        tempCanvas.width = smallWidth;
        tempCanvas.height = smallHeight;
        
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.drawImage(ctx.canvas, 0, 0, smallWidth, smallHeight);
        
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(tempCanvas, 0, 0, smallWidth, smallHeight, 0, 0, width, height);
        ctx.imageSmoothingEnabled = true;
    }, []);

    // 🎨 FILTRO DE COLOR
    const applyColorFilter = useCallback((ctx, width, height) => {
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        
        for (let i = 0; i < data.length; i += 4) {
            data[i] = Math.min(255, data[i] * 1.2); 
            data[i + 1] = Math.min(255, data[i + 1] * 1.1); // Verde
            data[i + 2] = Math.min(255, data[i + 2] * 0.9); // Azul
        }
        
        ctx.putImageData(imageData, 0, 0);
    }, []);

    // 🔴 NUEVO: EFECTO DIVISIÓN RGB (RGB SPLIT)
    const applyRGBSplitEffect = useCallback((ctx, width, height) => {
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        const newImageData = ctx.createImageData(width, height);
        const newData = newImageData.data;
        const offset = 5; // Cantidad de píxeles a desplazar

        for (let i = 0; i < data.length; i += 4) {
            const y = Math.floor((i / 4) / width);
            const x = (i / 4) % width;

            // Calcular el índice del píxel desplazado para Rojo (R) y Azul (B)
            // Se desplaza el Rojo hacia la izquierda y el Azul hacia la derecha

            // Índice para el Rojo (desplazado 5 a la derecha)
            const redIndex = (y * width + (x - offset)) * 4;

            // Índice para el Azul (desplazado 5 a la izquierda)
            const blueIndex = (y * width + (x + offset)) * 4;

            // Aplicar el desplazamiento si los índices están dentro de los límites
            
            // Canal ROJO (R)
            if (redIndex >= 0 && redIndex < data.length) {
                newData[i] = data[redIndex]; // Usar el canal R desplazado
            } else {
                newData[i] = data[i]; // Mantener el R original si está fuera de límite
            }

            // Canal VERDE (G) - Sin desplazamiento
            newData[i + 1] = data[i + 1];

            // Canal AZUL (B)
            if (blueIndex >= 0 && blueIndex < data.length) {
                newData[i + 2] = data[blueIndex + 2]; // Usar el canal B desplazado
            } else {
                newData[i + 2] = data[i + 2]; // Mantener el B original si está fuera de límite
            }

            // Canal Alfa (A) - Siempre se mantiene igual
            newData[i + 3] = data[i + 3];
        }

        ctx.putImageData(newImageData, 0, 0);
    }, []);

    // 💧 MARCA DE AGUA
    const addWatermark = useCallback((ctx, width, height) => {
        // Fondo del cintillo que abarca todo el ancho
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        const cintilloHeight = 65; // Un poco más de altura
        const cintilloY = height - 155; // Deja un margen inferior
        ctx.fillRect(0, cintilloY, width, cintilloHeight);

        // Dibujar la imagen si está cargada
        if (textImage) {
            const desiredWidth = 680;
            const aspectRatio = textImage.height / textImage.width;
            const desiredHeight = desiredWidth * aspectRatio;
            const textX = (width - desiredWidth) / 2; // Centrar la imagen reescalada
            const textY = cintilloY + (cintilloHeight - desiredHeight) / 2; // Centrar verticalmente
            ctx.drawImage(textImage, textX, textY, desiredWidth, desiredHeight);
        }
    }, [textImage]);

    const handleImageChange = useCallback((e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedFile(URL.createObjectURL(file));

            const userImg = new Image();
            const textImage = new Image();
            const logoImage = new Image();

            // Set sources using PUBLIC_URL for correct pathing in CRA
            userImg.src = URL.createObjectURL(file);
            textImage.src = process.env.PUBLIC_URL + '/assets/img/text.png';
            logoImage.src = process.env.PUBLIC_URL + '/LogoLynk&Co-02.png';

            const allImages = [userImg, textImage, logoImage];
            const promises = allImages.map(img => new Promise(resolve => img.onload = resolve));

            Promise.all(promises).then(() => {
                const canvas = canvasRef.current;
                const ctx = canvas.getContext('2d');
                canvas.width = userImg.width;
                canvas.height = userImg.height;
                ctx.drawImage(userImg, 0, 0);
                addWatermark(ctx, userImg.width, userImg.height, textImage, logoImage);
                setProcessedImage(canvas.toDataURL('image/png'));
            });
        }
    }, [addWatermark]);

// ----------------------------------------------------
// BUCLE PRINCIPAL DE DIBUJO
// ----------------------------------------------------
    
    // 🎭 APLICAR EFECTOS PRINCIPALES (BUCLE)
    const applyEffects = useCallback(() => {
        const video = webcamRef.current?.video;
        const canvas = canvasRef.current;
        
        if (!video || !canvas || video.readyState !== 4 || isCapturing) {
            if (!isCapturing) {
                animationRef.current = requestAnimationFrame(applyEffects);
            }
            return;
        }
        
        const width = video.videoWidth;
        const height = video.videoHeight;

        try {
            if (canvas.width !== width || canvas.height !== height) {
                canvas.width = width;
                canvas.height = height;
            }

            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            
            // 1. DIBUJAR VIDEO ACTUAL (base y limpieza)
            ctx.clearRect(0, 0, width, height);
            ctx.save();
            if (facingMode === 'user') {
                ctx.scale(-1, 1);
                ctx.drawImage(video, -width, 0, width, height);
            } else {
                ctx.drawImage(video, 0, 0, width, height);
            }
            ctx.restore();

            // 2. GUARDAR FRAME ACTUAL EN BUFFER
            const currentFrame = document.createElement('canvas');
            currentFrame.width = width;
            currentFrame.height = height;
            const frameCtx = currentFrame.getContext('2d');
            
            // Aseguramos que el frame guardado tenga el mismo espejo
            if (facingMode === 'user') {
                frameCtx.save();
                frameCtx.scale(-1, 1);
                frameCtx.drawImage(video, -width, 0, width, height);
                frameCtx.restore();
            } else {
                frameCtx.drawImage(video, 0, 0, width, height);
            }
            
            frameBufferRef.current.unshift(currentFrame);
            if (frameBufferRef.current.length > BUFFER_SIZE) {
                const oldFrame = frameBufferRef.current.pop();
                if (oldFrame) oldFrame.width = 0;
            }

            // 3. APLICAR EFECTOS
            // Nota: El orden de los efectos es crucial.
            // Los efectos de píxel (Pixelation, RGBSplit, ColorFilter) deben ir DESPUÉS del StreakBlur
            // o el StreakBlur debe usar un buffer ya afectado.

            // A. Aplicar Efecto Barrido (Usa el buffer de frames anteriores)
            if (activeEffects.streakBlur && frameBufferRef.current.length > 1) {
                applyStreakEffect(ctx, width, height);
            }

            // B. Aplicar efectos de manipulación de píxeles (se aplican al canvas actual)
            if (activeEffects.rgbSplit) { // 👈 NUEVA INTEGRACIÓN
                applyRGBSplitEffect(ctx, width, height);
            }
            if (activeEffects.pixelation) {
                applyPixelation(ctx, width, height);
            }
            if (activeEffects.colorEffects) {
                applyColorFilter(ctx, width, height);
            }

            // C. Aplicar Marca de Agua (Último paso)
            addWatermark(ctx, width, height);

            // 4. ESTADO DE CÁMARA LISTA
            framesProcessedRef.current++;
            if (!isCameraReadyRef.current && framesProcessedRef.current > FRAME_READY_THRESHOLD) {
                isCameraReadyRef.current = true;
                setCameraReady(true);
                setIsLoading(false);
            }

        } catch (error) {
            console.warn('⚠️ Error en bucle de efectos:', error);
        }

        animationRef.current = requestAnimationFrame(applyEffects);
    }, [activeEffects, facingMode, isCapturing, applyStreakEffect, applyPixelation, applyColorFilter, applyRGBSplitEffect, addWatermark]); // 👈 Dependencia RGB Split


// ----------------------------------------------------
// FUNCIÓN DE CAPTURA (CLAVE CORREGIDA)
// ----------------------------------------------------

    // 📸 CAPTURA DE FOTO (CORREGIDA Y ROBUSTA)
    const capturePhoto = useCallback(() => {
        if (isCapturing || !cameraReady) return;

        const video = webcamRef.current?.video;
        const canvas = canvasRef.current;
        
        if (!video || !canvas || video.readyState !== 4) {
            setError('Error: Recurso de video o canvas no disponible para captura.');
            return;
        }

        // 1. INICIAR EL ESTADO DE CAPTURA
        setIsCapturing(true);

        // 2. PAUSAR EL BUCLE DE ANIMACIÓN
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }
        
        // --- 3. DIBUJO MANUAL FORZADO (Último Frame con Efectos) ---
        const width = video.videoWidth;
        const height = video.videoHeight;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        
        // Limpieza y dibujo del frame base
        ctx.clearRect(0, 0, width, height);
        ctx.save();
        if (facingMode === 'user') {
            ctx.scale(-1, 1);
            ctx.drawImage(video, -width, 0, width, height);
        } else {
            ctx.drawImage(video, 0, 0, width, height);
        }
        ctx.restore();

        // Aplicar Efectos (usando el buffer ya lleno y el canvas actualizado)
        if (activeEffects.streakBlur && frameBufferRef.current.length > 1) {
            applyStreakEffect(ctx, width, height);
        }
        if (activeEffects.rgbSplit) { // 👈 Forzar aplicación en captura
            applyRGBSplitEffect(ctx, width, height);
        }
        if (activeEffects.pixelation) {
            applyPixelation(ctx, width, height);
        }
        if (activeEffects.colorEffects) {
            applyColorFilter(ctx, width, height);
        }
        addWatermark(ctx, width, height);
        // --- FIN DEL DIBUJO FORZADO ---
        
        // 4. CAPTURAR CON RETRASO
        setTimeout(() => {
            try {
                const imageSource = canvas.toDataURL('image/jpeg', 0.9);
                
                if (imageSource === 'data:,') {
                     // Caso de error extremo: el canvas está vacío o corrupto
                     console.error('❌ Error: toDataURL devolvió un string vacío.');
                     setError('Error de renderizado de la imagen. Intenta de nuevo.');
                     return;
                }

                setCapturedImage(imageSource);
                setShowPreview(true);
                
                if (navigator.vibrate) {
                    navigator.vibrate(50);
                }
            } catch (error) {
                console.error('❌ Error capturando:', error);
                setError('Error al capturar la foto. Intenta de nuevo.');
            } finally {
                // 5. REANUDAR LA ANIMACIÓN
                setIsCapturing(false);
                if (!animationRef.current) {
                    animationRef.current = requestAnimationFrame(applyEffects);
                }
            }
        }, CAPTURE_DELAY_MS);
        
    }, [isCapturing, cameraReady, applyEffects, facingMode, activeEffects, applyStreakEffect, applyPixelation, applyColorFilter, applyRGBSplitEffect, addWatermark]); // 👈 Dependencia RGB Split
    
// ----------------------------------------------------
// FUNCIONES DE CONTROL Y USEEFFECTS (MÍNIMOS AJUSTES)
// ----------------------------------------------------

    const startApp = () => {
        setShowPermissionModal(false);
        setError(null);
        setIsLoading(true);
        setCurrentView('filter');
    };

    const handleCameraError = useCallback((err) => {
        console.error('❌ Error en cámara:', err);
        let errorMessage = 'Error al acceder a la cámara. Revisa los permisos.';
        if (err.name === 'NotAllowedError') {
            errorMessage = 'Permiso de cámara denegado. Habilita el acceso en los ajustes del navegador.';
        }
        setError(errorMessage);
        setCameraActive(false);
        setIsLoading(false);
    }, []);

    const handleCameraReady = useCallback(() => {
        console.log('✅ Stream de cámara activo');
        setCameraActive(true);
    }, []);

    const flipCamera = useCallback(() => {
        if (isLoading) return;
        
        setIsLoading(true);
        setCameraActive(false);
        setCameraReady(false);
        isCameraReadyRef.current = false;
        framesProcessedRef.current = 0;
        frameBufferRef.current = []; // Limpiar buffer al cambiar cámara

        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }

        setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    }, [isLoading]);

    const handleRetry = () => {
        setError(null);
        setIsLoading(true);
        if (!cameraActive) {
            setCameraActive(true);
        }
    };

    const handleBackToLanding = () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }
        setCameraActive(false);
        setCameraReady(false);
        isCameraReadyRef.current = false;
        frameBufferRef.current = [];
        setCurrentView('landing');
    };

    const toggleEffect = (effectName) => {
        setActiveEffects(prev => ({
            ...prev,
            [effectName]: !prev[effectName]
        }));
    };

    const downloadPhoto = useCallback(() => {
        if (!capturedImage) return;

        const a = document.createElement('a');
        a.href = capturedImage;
        a.download = `lynkco-${Date.now()}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [capturedImage]);

    const sharePhoto = useCallback(async () => {
        if (!capturedImage) return;
        try {
            if (navigator.share) {
                const response = await fetch(capturedImage);
                const blob = await response.blob();
                const file = new File([blob], `lynkco-${Date.now()}.jpg`, { type: 'image/jpeg' });
                await navigator.share({
                    title: 'Lynk&Co Filter',
                    text: 'Drive What\'s Next 🚀',
                    files: [file]
                });
            } else {
                downloadPhoto();
            }
        } catch (error) {
            downloadPhoto();
        }
    }, [capturedImage, downloadPhoto]);

    // ⚡ EFFECTS (Inicio del bucle de animación)
    useEffect(() => {
        if (cameraActive && currentView === 'filter' && !animationRef.current) {
            const checkVideoAndStart = () => {
                if (webcamRef.current?.video?.readyState >= 3) {
                    framesProcessedRef.current = 0;
                    isCameraReadyRef.current = false;
                    setCameraReady(false);
                    setIsLoading(true);
                    frameBufferRef.current = []; 
                    animationRef.current = requestAnimationFrame(applyEffects);
                } else if (cameraActive) {
                    setTimeout(checkVideoAndStart, 100);
                }
            };
            checkVideoAndStart();
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
        };
    }, [cameraActive, currentView, applyEffects]);

// ----------------------------------------------------
// COMPONENTES DE UI (MANTENIDOS IGUAL)
// ----------------------------------------------------

    const PermissionModal = () => (
        <div className="modal-overlay" style={{ display: showPermissionModal ? 'flex' : 'none' }}>
            <div className="modal-content">
                <div className="modal-header">
                    <h2>Filtro LYNK&CO</h2>
                    
                </div>
                <div className="modal-body">
                    <p>Permite el acceso a tu cámara para usar el filtro.</p>
                    {error && <p style={{color: 'red', marginTop: '10px'}}>{error}</p>}
                </div>
                <div className="modal-actions">
                    <button className="modal-btn primary" onClick={startApp} disabled={isLoading || !!error}>
                        {isLoading ? 'Iniciando...' : 'Activar Camara'}
                    </button>
                    <button className="modal-btn secondary" onClick={() => setShowPermissionModal(false)}>
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );

    const LandingPage = () => (
        <div className="landing-page">
            <header>
                <div className="logo"> <img src="/LogoLynk&Co-02.png" alt="Lynk & Co" className="logo-img-tag" /> </div>
            </header>

            <section className="hero">
                <div className="hero-banner">
                    <div className="banner-overlay">
                        <div className="banner-content">
                            <button 
                                className="cta-button" 
                                onClick={() => setShowPermissionModal(true)}
                                disabled={isLoading}
                            >
                                <span>{isLoading ? 'Cargando...' : 'Activar Filtro'}</span>
                                <span>→</span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>
            <div className="info-container">
                <div className="info-text">
                    {!window.location.protocol.includes('https') && (
                        <p style={{color: 'red', textAlign: 'center', padding: '10px', backgroundColor: '#ff00002e'}}>
                            Atención: El acceso a la cámara requiere una conexión segura (HTTPS).
                        </p>
                    )}
                
                    <ul>
                         <li>Te invitamos a descubrir lo que<br></br> preparamos para ti.</li>
                        <li>Hemos creado el filtro oficial <strong >Drive What's Next</strong> para que juntos generemos expectativa antes del lanzamiento de la marca.</li>

                        <li><i>Solo podrás publicar y compartir las imágenes o videos capturados con el filtro oficial <strong >Drive What's Next.</strong></i></li>


                        <li>El contenido generado sin el filtro oficial deberá permanecer <strong  >bajo embargo hasta el 29 de octubre de 2025.</strong></li>
                    </ul>
                </div>
            </div>

            {showPermissionModal && <PermissionModal />}
        </div>
    );

    const FilterView = () => (
        <div className="filter-section">
            <div className="filter-header">
                <button className="back-button" onClick={handleBackToLanding}>
                    ← Volver
                </button>
                <div className="logo"> 
                    <img src="/LogoLynk&Co-02.png" alt="Lynk & Co" className="logo-img-tag" /> 
                </div>
             
            </div>
            
            <div className="camera-container">
                <Webcam
                    key={facingMode}
                    ref={webcamRef}
                    audio={false}
                    playsInline
                    muted 
                    screenshotFormat="image/jpeg"
                    videoConstraints={{
                        facingMode: facingMode,
                         height: { ideal: 720 },
                       width: { ideal: 1280 }
                       

                    }}
                    style={{
                        position: 'absolute',
                        zIndex: 1,
                        visibility: 'hidden',
                    }}
                    onUserMedia={handleCameraReady} 
                    onUserMediaError={handleCameraError}
                />

                <canvas 
                    ref={canvasRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 2,
                        pointerEvents: 'none',
                        objectFit: 'cover'
                    }}
                />

                {isLoading && (
                    <div className="loading-overlay">
                        <div className="loading-spinner"></div>
                        <p>Iniciando camara...</p>
                    </div>
                )}

                {error && (
                    <div className="error-overlay">
                        <p>{error}</p>
                        <div className="error-actions">
                            <button className="retry-button" onClick={handleRetry}>
                                REINTENTAR
                            </button>
                            <button className="back-button" onClick={handleBackToLanding}>
                                VOLVER
                            </button>
                        </div>
                    </div>
                )}
       


                <div className="controls-bottom">
                    <button 
                        className={`capture-btn ${isCapturing ? 'capturing' : ''} ${!cameraReady ? 'disabled' : ''}`}
                        onClick={capturePhoto}
                        disabled={!cameraReady || isCapturing}
                    >
                        <div className="capture-circle">
                            <div className="capture-inner"></div>
                        </div>
                        <span className="capture-text">
                            {isCapturing ? 'CAPTURANDO...' : cameraReady ? 'Capturar foto' : 'Cargando...'}
                        </span>
                    </button>
                </div>


             
            </div>

            {showPreview && capturedImage && (
                <div className="preview-overlay">
                    <div className="preview-content">
                        <div className="preview-header">
                            <h3>Foto Capturada</h3>
                            
                        </div>
                        
                        <div className="preview-image-container">
                            <img 
                                src={capturedImage} 
                                alt="Lynk&Co Professional Filter" 
                                className="preview-image"
                            />
                        </div>
                        
                        <div className="preview-actions">
                            <button className="preview-btn share-btn" onClick={sharePhoto}>
                                
                                <span>Descargar IOS</span>
                            </button>
                            
                            <button className="preview-btn download-btn" onClick={downloadPhoto}>
                               
                                <span>Descargar Android</span>
                            </button>
                            
                            <button 
                                className="preview-btn newphoto-btn" 
                                onClick={() => {
                                    setShowPreview(false);
                                    setCapturedImage(null);
                                }}
                            >
                                
                                <span>Nueva foto</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return currentView === 'landing' ? <LandingPage /> : <FilterView />;
}

export default App;